import { cn } from "@/utils";
import * as Dialog from "@radix-ui/react-dialog";
import { MdClose } from "react-icons/md";

interface Props {
  title?: string | React.ReactNode;
  open?: boolean;
  className?: string; // custom class for Dialog.Content
  contentClassName?: string; // custom class for content section
  overflowScroll?: boolean; // overflow-y-auto sẽ khiến checkbox multi select bị scroll nên thêm option overflowScroll để tùy chỉnh
  children?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  isStopPropagationOnModal?: boolean;
  isStopPropagationOnOverlay?: boolean;
  fullScreen?: boolean;
  displayCloseIcon?: boolean;
}

export function Modal(props: Props) {
  const {
    className,
    contentClassName,
    children,
    title,
    open,
    overflowScroll = true,
    onOpenChange,
    isStopPropagationOnModal = true,
    isStopPropagationOnOverlay = false,
    fullScreen = false,
    displayCloseIcon = false,
  } = props;

  // Handle modal clicks to prevent bubbling to parent elements
  const handleModalClick = (e: React.MouseEvent) => {
    if (isStopPropagationOnModal) e.stopPropagation();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close modal if clicking directly on overlay (not on content)
    // if (e.target === e.currentTarget) {
    //   onOpenChange(false);
    // }

    if (isStopPropagationOnOverlay) {
      e.stopPropagation();
      onOpenChange?.(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-dark/50 z-50 flex items-center justify-center"
          onClick={handleOverlayClick}
        >
          <Dialog.Content
            className={cn(
              "fixed z-50 border-0 outline-none",
              fullScreen
                ? "w-screen h-screen top-0 left-0"
                : "w-[90vw] lg:w-[42vw]",
              className
            )}
            onClick={handleModalClick}
            onPointerDownOutside={(e) => e.preventDefault()}
            onOpenAutoFocus={(event) => {
              event.preventDefault(); // Ngăn Radix focus input đầu tiên
            }}
          >
            <div
              className={cn(
                "shadow-lg border-0 outline-none",
                fullScreen ? "rounded-none" : "rounded-lg bg-white",
                contentClassName
              )}
            >
              {/* Close button  */}
              {displayCloseIcon && (
                <Dialog.Close asChild>
                  <button className="absolute right-4 top-4" aria-label="Close">
                    <MdClose className="size-6 text-light_secondary hover:opacity-75 transition-opacity" />
                  </button>
                </Dialog.Close>
              )}

              {/* title  */}
              {title ? (
                <Dialog.Title asChild>
                  <section
                    className={cn(
                      "bg-white w-full z-[51] px-4 border-b border-grey-300",
                      fullScreen ? "rounded-none" : "rounded-t-lg"
                    )}
                  >
                    <div className="text-lg font-bold leading-7 py-4">
                      {title}
                    </div>
                  </section>
                </Dialog.Title>
              ) : (
                // fix lỗi trên console khi không có title
                <div className="invisible">
                  <Dialog.Title>Modal</Dialog.Title>
                </div>
              )}
              {/* content  */}
              <section
                className={cn(
                  "p-4",
                  overflowScroll && "overflow-y-auto",
                  fullScreen
                    ? "min-h-[calc(100vh-61px)] !px-5 h-[calc(100vh-61px)]" // theo design modal full screen có padding-x 20px
                    : "max-h-[calc(100vh-8rem)]"
                )}
              >
                {children}
              </section>
            </div>
            <Dialog.Description></Dialog.Description>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
